import { animated, config, useSpring, useSprings } from '@react-spring/web';
import { CSSProperties, memo, useEffect, useMemo, useState } from 'react';
import CountUp from 'react-countup';

import { useRewindContext } from '../../../rewindModal.context';
import { RewindSlideProps } from '../../../rewindModal.types';
import { SlideContainer } from '../../slideContainer';

import styles from './winsSlide.module.scss';

// Animation timing constants (in ms from slide start)
const TIMING = {
  // Phase 1: Big title appears centered
  TITLE_START: 300,
  // Phase 2: Title moves to top
  TITLE_MOVE_TO_TOP: 2000,
  // Phase 3: Subtitle appears and slides up from below title
  SUBTITLE_APPEAR: 2500,
  // Phase 4: CSAT ring appears in center
  CSAT_RING_APPEAR: 4000,
  // Phase 5: CSAT ring fills with animation
  CSAT_RING_FILL: 5000,
  // Phase 6: Tier badge appears (after ring animation completes)
  TIER_BADGE_APPEAR: 8000,
  // Phase 7: CSAT ring moves to left
  CSAT_RING_MOVE: 9000,
  // Phase 8-10: Rating cards appear one by one
  RATING_CARD_1: 10500,
  RATING_CARD_2: 12000,
  RATING_CARD_3: 13500,
  // Phase 11: Motivational text appears at bottom
  MOTIVATION_APPEAR: 15000,
};

// CSAT Tiers with colors and messages
type CsatTier =
  | 'legendary'
  | 'excellent'
  | 'great'
  | 'good'
  | 'improving'
  | 'unknown';

interface TierConfig {
  color: string;
  glowColor: string;
  secondaryColor: string;
  emoji: string;
  title: string;
  motivation: string;
  ratingMessage: string;
}

const CSAT_TIERS: Record<CsatTier, TierConfig> = {
  legendary: {
    color: '#FFD700',
    glowColor: 'rgba(255, 215, 0, 0.8)',
    secondaryColor: '#FFA500',
    emoji: '👑',
    title: 'LEGENDARY',
    motivation: "You're in the top tier. Customers absolutely love you!",
    ratingMessage: 'Your ratings speak volumes about your dedication',
  },
  excellent: {
    color: '#00FF88',
    glowColor: 'rgba(0, 255, 136, 0.8)',
    secondaryColor: '#00FFFF',
    emoji: '🌟',
    title: 'EXCELLENT',
    motivation: 'Outstanding performance. Keep shining bright!',
    ratingMessage: 'Customers consistently rate you among the best',
  },
  great: {
    color: '#00FFFF',
    glowColor: 'rgba(0, 255, 255, 0.8)',
    secondaryColor: '#FF00FF',
    emoji: '💫',
    title: 'GREAT',
    motivation: "You're making real impact. Great work this year!",
    ratingMessage: 'Your ratings show consistent quality',
  },
  good: {
    color: '#FF00FF',
    glowColor: 'rgba(255, 0, 255, 0.8)',
    secondaryColor: '#00FFFF',
    emoji: '⭐',
    title: 'SOLID',
    motivation: "Every conversation matters. You're building something great!",
    ratingMessage: 'Each rating is a stepping stone to excellence',
  },
  improving: {
    color: '#FF6B6B',
    glowColor: 'rgba(255, 107, 107, 0.8)',
    secondaryColor: '#FFD93D',
    emoji: '🚀',
    title: 'RISING',
    motivation: 'Every great journey starts somewhere. Keep pushing!',
    ratingMessage: "You're on the path to improvement. Keep it up!",
  },
  unknown: {
    color: '#BF00FF',
    glowColor: 'rgba(191, 0, 255, 0.8)',
    secondaryColor: '#00FFFF',
    emoji: '✨',
    title: 'MYSTERY CHAMPION',
    motivation: 'Your impact goes beyond numbers. Every conversation matters!',
    ratingMessage: 'Your dedication shines through in every interaction',
  },
};

const getCsatTier = (score: number | null | undefined): CsatTier => {
  if (!score) return 'unknown';
  if (score >= 90) return 'legendary';
  if (score >= 80) return 'excellent';
  if (score >= 70) return 'great';
  if (score >= 60) return 'good';
  return 'improving';
};

const getStarRatingMessage = (
  fiveStarCount: number,
  totalRatings: number,
): string => {
  if (totalRatings === 0) return 'Ready to collect your first ratings';
  const fiveStarPercentage = (fiveStarCount / totalRatings) * 100;
  if (fiveStarPercentage >= 80) return 'A constellation of 5-star reviews';
  if (fiveStarPercentage >= 50)
    return 'More than half your ratings are 5 stars';
  if (fiveStarCount > 0) return `${fiveStarCount} customers gave you top marks`;
  return 'Your ratings show room for a stellar comeback';
};

export const WinsSlide = memo(function WinsSlide({
  isCompleted,
}: RewindSlideProps): JSX.Element {
  const { data } = useRewindContext();
  const [phase, setPhase] = useState(isCompleted ? 11 : 0);

  // Determine tier based on CSAT score
  const tier = useMemo(
    () => getCsatTier(data?.csat_score?.agent),
    [data?.csat_score?.agent],
  );
  const tierConfig = CSAT_TIERS[tier];

  // Calculate display values
  const csatDisplay = data?.csat_score?.agent ?? 0;
  const hasCSAT = !!data?.csat_score?.agent;
  const totalRatings = data?.total_ratings_received.agent ?? 0;
  const fiveStarCount = data?.five_star_ratings_count.agent ?? 0;

  // Start animation sequence
  useEffect(() => {
    // If slide has completed before, skip animations
    if (isCompleted) {
      setPhase(11);
      return;
    }

    const timers = [
      setTimeout(() => setPhase(1), TIMING.TITLE_START),
      setTimeout(() => setPhase(2), TIMING.TITLE_MOVE_TO_TOP),
      setTimeout(() => setPhase(3), TIMING.SUBTITLE_APPEAR),
      setTimeout(() => setPhase(4), TIMING.CSAT_RING_APPEAR),
      setTimeout(() => setPhase(5), TIMING.CSAT_RING_FILL),
      setTimeout(() => setPhase(6), TIMING.TIER_BADGE_APPEAR),
      setTimeout(() => setPhase(7), TIMING.CSAT_RING_MOVE),
      // Rating cards appear one by one
      setTimeout(() => setPhase(8), TIMING.RATING_CARD_1),
      setTimeout(() => setPhase(9), TIMING.RATING_CARD_2),
      setTimeout(() => setPhase(10), TIMING.RATING_CARD_3),
      setTimeout(() => setPhase(11), TIMING.MOTIVATION_APPEAR),
    ];

    return () => timers.forEach(clearTimeout);
  }, [isCompleted]);

  // Title animation - exactly like style slide
  const titleSpring = useSpring({
    opacity: phase >= 1 ? 1 : 0,
    transform:
      phase >= 2
        ? 'translateY(-280px) scale(0.5)'
        : phase >= 1
          ? 'translateY(0px) scale(1)'
          : 'translateY(50px) scale(0.8)',
    config: { tension: 120, friction: 14 },
    immediate: isCompleted,
  });

  // Subtitle animation - exactly like style slide
  const subtitleSpring = useSpring({
    opacity: phase >= 3 ? 1 : 0,
    transform:
      phase >= 3
        ? 'translateY(-320px) scale(0.6)'
        : 'translateY(-360px) scale(0.6)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // CSAT ring container animation - starts perfectly centered, scales down when badge appears, moves left when cards arrive
  // Card container is 420px + 60px gap = 480px, so offset by half (240px) to center ring initially
  const csatContainerSpring = useSpring({
    opacity: phase >= 4 ? 1 : 0,
    transform:
      phase >= 8
        ? 'translateX(0px) scale(0.85)'
        : phase >= 6
          ? 'translateX(240px) scale(0.85)'
          : phase >= 4
            ? 'translateX(240px) scale(1)'
            : 'translateX(240px) scale(0.5)',
    config: { tension: 80, friction: 14 },
    immediate: isCompleted,
  });

  // CSAT ring progress animation
  const ringProgress = useSpring({
    value: phase >= 5 ? (hasCSAT ? csatDisplay : 100) : 0,
    config: { duration: isCompleted ? 0 : 1500 },
    immediate: isCompleted,
  });

  // Rating cards animation - staggered entrance from right
  const ratingCards = [
    {
      icon: '⭐',
      title: 'Total Ratings',
      value: totalRatings,
      subtitle: 'voices heard throughout the year',
    },
    {
      icon: '🌟',
      title: '5-Star Reviews',
      value: fiveStarCount,
      subtitle: getStarRatingMessage(fiveStarCount, totalRatings),
    },
    {
      icon: tierConfig.emoji,
      title: 'Your Status',
      value: tierConfig.title,
      subtitle: tierConfig.ratingMessage,
      isText: true,
    },
  ];

  // Each card has its own phase: card 1 at phase 8, card 2 at phase 9, card 3 at phase 10
  // Using ease-in style config - starts slow and accelerates (lower friction = faster end)
  const ratingCardSprings = useSprings(3, [
    {
      opacity: phase >= 8 ? 1 : 0,
      transform: phase >= 8 ? 'translateX(0px)' : 'translateX(300px)',
      config: config.gentle,
      immediate: isCompleted,
    },
    {
      opacity: phase >= 9 ? 1 : 0,
      transform: phase >= 9 ? 'translateX(0px)' : 'translateX(300px)',
      config: config.gentle,
      immediate: isCompleted,
    },
    {
      opacity: phase >= 10 ? 1 : 0,
      transform: phase >= 10 ? 'translateX(0px)' : 'translateX(300px)',
      config: config.gentle,
      immediate: isCompleted,
    },
  ]);

  // Motivation text animation
  const motivationSpring = useSpring({
    opacity: phase >= 11 ? 1 : 0,
    transform: phase >= 11 ? 'translateY(0px)' : 'translateY(40px)',
    config: config.gentle,
    immediate: isCompleted,
  });

  // SVG circle calculations
  const circleRadius = 185;
  const circleCircumference = 2 * Math.PI * circleRadius;

  return (
    <SlideContainer
      className={isCompleted ? styles.completed : undefined}
      style={
        {
          '--tier-color': tierConfig.color,
          '--tier-glow': tierConfig.glowColor,
          '--tier-secondary': tierConfig.secondaryColor,
        } as CSSProperties
      }
    >
      <animated.div className={styles.titleWrapper} style={titleSpring}>
        <h1
          className={`${styles.title} ${phase >= 1 ? styles.glitchActive : ''}`}
        >
          <span className={styles.glitchText} data-text="YOUR WINS">
            YOUR WINS
          </span>
        </h1>
      </animated.div>

      <animated.p className={styles.subtitle} style={subtitleSpring}>
        WHEN YOU SHINED BRIGHTEST
      </animated.p>

      {/* Main content area */}
      <div
        className={`${styles.mainContent} ${phase >= 4 ? styles.visible : ''}`}
      >
        {/* CSAT Ring */}
        <animated.div
          className={styles.csatRingContainer}
          style={csatContainerSpring}
        >
          <div
            className={`${styles.csatRing} ${phase >= 5 ? styles.filling : ''}`}
          >
            <svg className={styles.progressRing} viewBox="0 0 420 420">
              {/* Background circle */}
              <circle
                className={styles.progressBg}
                cx="210"
                cy="210"
                r={circleRadius}
                strokeWidth="18"
              />
              {/* Progress circle */}
              <animated.circle
                className={styles.progressFill}
                cx="210"
                cy="210"
                r={circleRadius}
                strokeWidth="18"
                strokeDasharray={circleCircumference}
                strokeDashoffset={ringProgress.value.to(
                  (val) =>
                    circleCircumference - (val / 100) * circleCircumference,
                )}
                style={{
                  stroke: tierConfig.color,
                  filter: `drop-shadow(0 0 10px ${tierConfig.glowColor})`,
                }}
              />
            </svg>
            <div className={styles.csatCenter}>
              {hasCSAT ? (
                <>
                  <span className={styles.csatValue}>
                    {phase >= 5 ? (
                      <CountUp
                        end={csatDisplay}
                        duration={isCompleted ? 0 : 1.5}
                        decimals={0}
                      />
                    ) : (
                      '0'
                    )}
                  </span>
                  <span className={styles.csatLabel}>CSAT SCORE</span>
                </>
              ) : (
                <>
                  <span className={styles.csatEmoji}>{tierConfig.emoji}</span>
                  <span className={styles.csatLabel}>CSAT</span>
                  <span className={styles.csatNoData}>Beyond Measure</span>
                </>
              )}
            </div>
          </div>
          <div
            className={`${styles.tierBadge} ${phase >= 6 ? styles.visible : ''}`}
          >
            <span className={styles.tierEmoji}>{tierConfig.emoji}</span>
            <span className={styles.tierTitle}>{tierConfig.title}</span>
          </div>
        </animated.div>

        {/* Rating Cards */}
        <div
          className={`${styles.ratingCardsContainer} ${phase >= 8 ? styles.visible : ''}`}
        >
          {ratingCards.map((card, index) => (
            <animated.div
              key={card.title}
              className={styles.ratingCard}
              style={ratingCardSprings[index]}
            >
              <span className={styles.cardIcon}>{card.icon}</span>
              <span className={styles.cardTitle}>{card.title}</span>
              <span className={styles.cardValue}>
                {card.isText ? (
                  card.value
                ) : phase >= 8 + index ? (
                  <CountUp
                    end={card.value as number}
                    duration={isCompleted ? 0 : 1.2}
                    delay={0}
                  />
                ) : (
                  '0'
                )}
              </span>
              <span className={styles.cardSubtitle}>{card.subtitle}</span>
            </animated.div>
          ))}
        </div>
      </div>

      {/* Motivational footer */}
      <animated.div
        className={`${styles.motivationSection} ${phase >= 11 ? styles.visible : ''}`}
        style={motivationSpring}
      >
        <p className={styles.motivationText}>{tierConfig.motivation}</p>
      </animated.div>
    </SlideContainer>
  );
});
